'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import ContentTitle from '@/components/ContentTitle';
import MovieDescription from '@/components/MovieDescription';
import ShowingListItemImage from '@/components/ShowingListItemImage';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { CachedContentData } from '@/hooks/useCachedContentData';

import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';

import { Link } from '@/i18n/routing';

import AllShowingDaysSlider from './AllShowingDaysSlider';

type ShowingListItemProps = {
    content: CachedContentData;
    selectedDate?: string | 'all';
    /** This content's showings for the selected date, already filtered by the list. */
    showings: ShowingDto[];
    isFetchingShowingList: boolean;
};

/**
 * One tree for both breakpoints: the grid stacks title, backdrop, count and slider on mobile and
 * lays poster | text | slider side by side from `lg`. Only the two images are breakpoint-specific,
 * and the hidden one is a lazy `next/image` that never loads. Rendering a full desktop row and a
 * full mobile row and hiding one with CSS doubled every slider and ticket button on the page.
 */
export default function ShowingListItem(props: ShowingListItemProps) {
    const { content, selectedDate, showings, isFetchingShowingList } = props;
    const t = useTranslations();

    const href: HrefOrHrefWithParams<'/program/[contentId]'> = {
        pathname: '/program/[contentId]',
        params: {
            contentId: content.id
        }
    };

    return (
        <div
            className="grid grid-cols-1 gap-y-12 py-16 lg:grid-cols-[120px_480px_minmax(0,1fr)] lg:grid-rows-[auto_1fr_auto] lg:gap-x-24 lg:gap-y-16 lg:py-24"
            role="group"
            aria-label={content.title}>
            <Link
                className="hidden lg:block lg:row-span-3 cursor-pointer w-auto h-full min-w-[120px] aspect-[2/3]"
                href={href}
                aria-label={content.title}
                prefetch={false}>
                <ImageWithTransition
                    className="rounded-4"
                    width={120}
                    height={180}
                    src={content.posterImage ?? ''}
                    alt={content.title ?? ''}
                />
            </Link>
            <div className="lg:col-start-2 lg:row-start-1">
                <ContentTitle
                    href={href}
                    content={content}
                    showMovieDuration
                    showGenres
                />
            </div>
            <div className="lg:hidden">
                <ShowingListItemImage
                    href={href}
                    content={content}
                />
            </div>
            <Link
                href={href}
                className="hidden lg:block lg:col-start-2 lg:row-start-2 max-w-[600px] text-[rgba(var(--on-background-color-rgb),0.8)]">
                <MovieDescription
                    content={content}
                    maxLinesDesktop={3}
                />
            </Link>
            <div className="lg:col-start-2 lg:row-start-3 text-body-bold-12 italic lg:text-[rgba(var(--on-background-color-rgb),0.8)]">
                {t('specials.n-showings', { count: showings.length })}
            </div>
            <div className="flex min-w-0 lg:col-start-3 lg:row-start-1 lg:row-span-3">
                <AllShowingDaysSlider
                    content={content}
                    selectedDate={selectedDate}
                    showings={showings}
                    isFetchingShowingList={isFetchingShowingList}
                />
            </div>
        </div>
    );
}
